import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import Header from "./Header";
import Footer from "./Footer";
import OpenToWorkBanner from "./OpenToWorkBanner";

const LayoutWrapper = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

/**
 * Banner + nav as one fixed stack.
 *
 * They used to be two independently fixed bars, with the nav's `top` hardcoded to
 * whatever the banner happened to measure. Any change to the banner's contents left
 * a gap or an overlap. Stacking them lets the nav sit directly under the banner at
 * whatever height it takes, and the measured total drives the page's top padding.
 *
 * Hiding uses `top`, deliberately not `transform`: a transformed ancestor becomes the
 * containing block for `position: fixed` descendants, which would break the mobile
 * nav drawer that expects to be laid out against the viewport.
 */
const ChromeStack = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    z-index: 1000;
    top: ${({ $hidden, $height }) => ($hidden ? `-${$height || 200}px` : "0")};
    opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
    transition: top 0.6s ease, opacity 0.6s ease;
`;

const MainContent = styled.main`
    flex: 1;
    position: relative;
    /* Reserve exactly the chrome's measured height; the fallback covers first paint. */
    padding-top: var(--chrome-h, 145px);
`;

const Layout = ({ children, darkMode, toggleDarkMode, hideChrome = false }) => {
    const chromeRef = useRef(null);
    const [height, setHeight] = useState(0);

    const measure = useCallback(() => {
        const el = chromeRef.current;
        if (!el) return;
        // Round up: the stack can measure fractionally (130.3px at some widths), and
        // rounding down would tuck the first line of content under the nav.
        setHeight(Math.ceil(el.getBoundingClientRect().height));
    }, []);

    useLayoutEffect(() => {
        measure();
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);
            return () => window.removeEventListener("resize", measure);
        }
        const observer = new ResizeObserver(measure);
        if (chromeRef.current) observer.observe(chromeRef.current);
        return () => observer.disconnect();
    }, [measure]);

    // Published as a custom property so the hero can size itself against the same
    // number instead of keeping its own copy of it.
    useEffect(() => {
        if (!height) return;
        document.documentElement.style.setProperty("--chrome-h", `${height}px`);
    }, [height]);

    return (
        <LayoutWrapper>
            <ChromeStack ref={chromeRef} $hidden={hideChrome} $height={height}>
                <OpenToWorkBanner />
                <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            </ChromeStack>
            <MainContent>{children}</MainContent>
            <Footer />
        </LayoutWrapper>
    );
};

export default Layout;
